import type { AxiosInstance } from 'axios';
import { isGatewayRoutable, resolveUpstream } from './server/allowlist';
import { ACT_AS_DOCTOR_HEADER, getRoutingDoctor, SECRETARY_DOCTOR_HEADER, SECRETARY_GATEWAY_PREFIX } from './gateway';

/** axios joins base and url like this; we need the absolute address to know where the call is going */
const absoluteUrl = (baseURL: string | undefined, url: string | undefined) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  if (!baseURL) return null;
  return `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
};

/**
 * Secretary mode, for the app's shared clients: while a secretary acts for a doctor, a call made on the doctor's side of the app (see ACTING_ROUTES) that the gateway's allow-list
 * could serve goes there (same origin, the doctor's data, the doctor's token added on the server). Everything else —
 * the secretary's own login check, notifications, chat, search — is left exactly as it was.
 */
export const installSecretaryRouting = (...clients: AxiosInstance[]) => {
  clients.forEach(client =>
    client.interceptors.request.use(config => {
      const forced = config.headers?.has?.(ACT_AS_DOCTOR_HEADER) ?? false;
      config.headers?.delete?.(ACT_AS_DOCTOR_HEADER); // an internal marker, never sent
      const doctorId = getRoutingDoctor(forced);
      if (!doctorId) return config;

      const absolute = absoluteUrl(config.baseURL, config.url);
      const upstream = absolute ? resolveUpstream(absolute) : null;
      if (!upstream || !isGatewayRoutable(upstream.service, config.method ?? 'get', upstream.path)) return config;

      config.baseURL = undefined;
      config.url = `${SECRETARY_GATEWAY_PREFIX}/${upstream.service}${upstream.path}${upstream.search}`;
      config.headers.set(SECRETARY_DOCTOR_HEADER, doctorId);
      return config;
    }),
  );
};
